import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import urls from "../../../../global/constants/UrlConstants";
import PersonalDetails from "../stepper/Personal Details/PersonalDetails";
import OfficialInformation from "../stepper/OfficialInformation/OfficialInformation";
import BankInformation from "../stepper/BankInformation/BankInformation";
import EmployeeDocuments from "../stepper/EmployeeDocuments/EmployeeDocuments";
import LeaveDetails from "../stepper/LeaveDetails/LeaveDetails";
import SalaryInformation from "../stepper/SalaryInformation/SalaryInformation";
import { openErrorNotification } from "../../../../helpers/methods";
import YhButton from "../../../../ui/YhButton";

const stepData = [
  "Personal Details",
  "Official Information",
  "Bank Information",
  "Employee Documents",
  "Leave Details",
  "Salary Information",
];

export type StepActions = {
  submit: () => void | Promise<void>;
};

interface CustomProps {
  isDialogOpen?: boolean;
  handleCloseAddTeam?: Function;
  userId?: string;
  setUserId?: Function;
  fromSettingOrLogout?: string;
}

const AddNewTeam = (props: CustomProps = {}) => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<{ id?: string }>();
  const isEmbedded = props.isDialogOpen !== undefined;
  const isArchiveFlow = location.pathname.includes("/archive-member");

  const [userId, setUserId] = useState<string>(props.userId || id || "");
  const [activeStep, setActiveStep] = useState<number>(0);
  const [completed] = useState<{ [k: number]: boolean }>({});
  const [saving, setSaving] = useState(false);
  const stepActionsRef = useRef<StepActions | null>(null);

  useEffect(() => {
    if (props.userId) setUserId(props.userId);
    else if (id) setUserId(id);
  }, [props.userId, id]);

  useEffect(() => {
    stepActionsRef.current = null;
  }, [activeStep]);

  const registerStepActions = useCallback((actions: StepActions) => {
    stepActionsRef.current = actions;
  }, []);

  const totalSteps = () => stepData.length;
  const completedSteps = () => Object.keys(completed).length;
  const allStepsCompleted = () => completedSteps() === totalSteps();
  const isLastStep = () => activeStep === totalSteps() - 1;

  const handleClose = () => {
    if (props.handleCloseAddTeam) {
      props.handleCloseAddTeam();
      return;
    }
    history.push(isArchiveFlow ? urls.archiveMember : urls.teamViewPath);
  };

  const updateUserId = (nextId: string) => {
    setUserId(nextId);
    props.setUserId?.(nextId);
  };

  const nextStep = (next = activeStep) => {
    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? stepData.findIndex((_, i) => !(i in completed))
        : next + 1;
    setActiveStep(newActiveStep);
  };

  const backStep = () => {
    setActiveStep((s) => Math.max(0, s - 1));
  };

  const handleFooterBack = () => {
    if (activeStep === 0) {
      handleClose();
      return;
    }
    backStep();
  };

  const handleFooterSave = async () => {
    if (!stepActionsRef.current?.submit) return;
    try {
      setSaving(true);
      await stepActionsRef.current.submit();
    } finally {
      setSaving(false);
    }
  };

  const handleNextValidation = (index: number) => {
    try {
      if (!userId.length && index > 0) {
        throw new Error("Please Register user");
      }
      setActiveStep(index);
      if (index > 0) nextStep(index - 1);
    } catch {
      openErrorNotification("Please Register user");
    }
  };

  const getAllContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <PersonalDetails
            userId={userId}
            nextStep={nextStep}
            setUserId={updateUserId}
            fromSettingOrLogout={props.fromSettingOrLogout}
            registerStepActions={registerStepActions}
            hideFooterButtons
          />
        );
      case 1:
        return (
          <OfficialInformation
            userId={userId}
            nextStep={nextStep}
            backStep={backStep}
            fromSettingOrLogout={props.fromSettingOrLogout}
            registerStepActions={registerStepActions}
            hideFooterButtons
          />
        );
      case 2:
        return (
          <BankInformation
            userId={userId}
            nextStep={nextStep}
            backStep={backStep}
            fromSettingOrLogout={props.fromSettingOrLogout}
            registerStepActions={registerStepActions}
            hideFooterButtons
          />
        );
      case 3:
        return (
          <EmployeeDocuments
            nextStep={nextStep}
            backStep={backStep}
            userId={userId}
            fromSettingOrLogout={props.fromSettingOrLogout}
            registerStepActions={registerStepActions}
            hideFooterButtons
          />
        );
      case 4:
        return (
          <LeaveDetails
            userId={userId}
            nextStep={nextStep}
            backStep={backStep}
            fromSettingOrLogout={props.fromSettingOrLogout}
            registerStepActions={registerStepActions}
            hideFooterButtons
          />
        );
      case 5:
        return (
          <SalaryInformation
            userId={userId}
            nextStep={nextStep}
            backStep={backStep}
            handleCloseAddTeam={handleClose}
            fromSettingOrLogout={props.fromSettingOrLogout}
            registerStepActions={registerStepActions}
            hideFooterButtons
          />
        );
      default:
        return null;
    }
  };

  const title = props.fromSettingOrLogout
    ? "My Profile"
    : userId
      ? "Edit Team Member"
      : "Add Team Member";

  const subtitle = props.fromSettingOrLogout
    ? "View and update your profile details."
    : "Complete each step to save team member details.";

  const stickyFooter = (
    <div className="sticky bottom-0 z-20 -mx-4 mt-auto flex shrink-0 items-center justify-between gap-3 border-t border-[#EEF0F5] bg-white/95 px-4 py-3 backdrop-blur sm:-mx-5 sm:px-5 lg:-mx-6 lg:px-6">
      <button
        type="button"
        onClick={handleFooterBack}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#374151] shadow-sm transition hover:bg-[#F3F4F6]"
        aria-label="Back"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
      <div className="flex shrink-0 items-center gap-2">
        {activeStep > 0 ? (
          <YhButton
            variant="secondary"
            size="sm"
            onClick={handleFooterBack}
          >
            Back
          </YhButton>
        ) : null}
        <YhButton
          variant="primary"
          size="sm"
          loading={saving}
          onClick={handleFooterSave}
        >
          {isLastStep() ? "Save" : "Save & Continue"}
        </YhButton>
      </div>
    </div>
  );

  const formBody = (
    <div
      className={`relative flex flex-col overflow-hidden bg-white p-4 pb-0 sm:p-5 sm:pb-0 lg:p-6 lg:pb-0 ${
        isEmbedded
          ? "h-[min(90vh,880px)] w-full max-w-6xl"
          : "h-[calc(100vh-68px)] max-h-[calc(100vh-68px)]"
      }`}
    >
      <div className="mb-4 flex shrink-0 items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#111827]">
            {title}
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">{subtitle}</p>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 gap-4 overflow-hidden pb-20">
        <aside className="hidden w-[240px] shrink-0 overflow-auto rounded-2xl border border-[#EEF0F5] bg-white p-4 shadow-[0_4px_20px_rgba(15,23,42,0.04)] md:block">
          <ol className="space-y-1">
            {stepData.map((label, index) => {
              const isActive = index === activeStep;
              const isDone = index < activeStep || !!completed[index];
              return (
                <li key={label}>
                  <button
                    type="button"
                    onClick={() => handleNextValidation(index)}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition ${
                      isActive
                        ? "bg-[#F9F0F4] font-semibold text-[#841C39]"
                        : isDone
                          ? "text-[#111827] hover:bg-[#F8FAFC]"
                          : "text-[#6B7280] hover:bg-[#F8FAFC]"
                    }`}
                  >
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                        isActive
                          ? "bg-[#A02448] text-white"
                          : isDone
                            ? "bg-[#D1FAE5] text-[#059669]"
                            : "bg-[#F3F4F6] text-[#6B7280]"
                      }`}
                    >
                      {isDone && !isActive ? "✓" : index + 1}
                    </span>
                    <span className="leading-snug">{label}</span>
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>

        <section className="min-h-0 min-w-0 flex-1 overflow-auto rounded-2xl border border-[#EEF0F5] bg-white p-4 shadow-[0_4px_20px_rgba(15,23,42,0.04)] sm:p-6">
          <div className="mb-4 flex gap-2 overflow-x-auto md:hidden">
            {stepData.map((label, index) => (
              <button
                key={label}
                type="button"
                onClick={() => handleNextValidation(index)}
                className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium ${
                  index === activeStep
                    ? "bg-[#A02448] text-white"
                    : "bg-[#F3F4F6] text-[#6B7280]"
                }`}
              >
                {index + 1}. {label}
              </button>
            ))}
          </div>
          {getAllContent(activeStep)}
        </section>
      </div>

      {stickyFooter}
    </div>
  );

  if (isEmbedded) {
    if (!props.isDialogOpen) return null;
    return (
      <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-6xl overflow-hidden rounded-2xl shadow-xl">
          {formBody}
        </div>
      </div>
    );
  }

  return formBody;
};

export default AddNewTeam;
